import { liveQuery } from "dexie";
import { useCallback, useEffect, useMemo, useState } from "react";

import { getGatewayBaseUrl } from "../config";
import type { CatalogProductMediaProjection } from "../gateway/catalog-price-client";
import {
  PosCatalogMediaGatewayClient,
  type CatalogMediaDelivery,
} from "../gateway/catalog-media-client";
import type { LocalCatalogMenu } from "../local-store/catalog-price-runtime";
import {
  catalogMediaReferenceKey,
  catalogMediaScope,
  pruneCatalogMediaScope,
  purgeCatalogMediaOutsideScope,
  readCatalogMediaForScope,
  storeVerifiedCatalogMedia,
} from "../local-store/catalog-media-runtime";
import { readLocalEmployeeSession } from "../local-store/employee-session";
import { getPosRuntimePerformanceProfile } from "../performance/runtime-performance-profile";
import type { TerminalCredentialState } from "../local-store/terminal-state";
import { useRuntimeAutoSync } from "../sync/use-runtime-auto-sync";

const MEDIA_SYNC_INTERVAL_MS = 60_000;
const MEDIA_AUTH_BATCH_SIZE = 256;

function primaryMediaFromMenus(menus: LocalCatalogMenu[]) {
  const unique = new Map<string, CatalogProductMediaProjection>();
  for (const menu of menus) {
    const media = menu.snapshot.product_media?.primary_media;
    if (!media?.media_asset_id || !media.content_revision) continue;
    const key = catalogMediaReferenceKey(media.media_asset_id, media.content_revision);
    if (!unique.has(key)) unique.set(key, media);
  }

  // Preserve the Published Local Catalog menu order. The sale grid consumes the same
  // ordered menu runtime, so a cold terminal fills the first sale cards before media
  // that appears later in the catalog instead of sorting downloads by opaque asset ID.
  return Array.from(unique.values());
}

export function usePosCatalogMediaRuntime(
  terminal: TerminalCredentialState,
  online: boolean,
  menus: LocalCatalogMenu[],
) {
  const identity = terminal.identity;
  const requiredMedia = useMemo(() => primaryMediaFromMenus(menus), [menus]);
  const requiredKey = useMemo(
    () => requiredMedia
      .map((media) => catalogMediaReferenceKey(media.media_asset_id, media.content_revision))
      .sort()
      .join("|"),
    [requiredMedia],
  );
  const scopeKey = identity ? catalogMediaScope(identity) : "";
  const mediaDownloadConcurrency = getPosRuntimePerformanceProfile().mediaDownloadConcurrency;
  const [objectUrls, setObjectUrls] = useState<Map<string, string>>(() => new Map());
  const [syncError, setSyncError] = useState<string | null>(null);

  useEffect(() => {
    if (!identity) {
      setObjectUrls(new Map());
      return;
    }

    const subscription = liveQuery(() => readCatalogMediaForScope(identity)).subscribe({
      next: (rows) => {
        setObjectUrls((previous) => {
          const next = new Map<string, string>();
          for (const row of rows) {
            const key = catalogMediaReferenceKey(row.mediaAssetId, row.contentRevision);
            const existingUrl = previous.get(key);
            next.set(key, existingUrl ?? URL.createObjectURL(row.blob));
          }

          for (const [key, url] of previous) {
            if (!next.has(key)) URL.revokeObjectURL(url);
          }
          return next;
        });
      },
    });

    return () => {
      subscription.unsubscribe();
      setObjectUrls((previous) => {
        for (const url of previous.values()) URL.revokeObjectURL(url);
        return new Map();
      });
    };
  }, [identity]);

  useEffect(() => {
    if (!identity) return;
    void purgeCatalogMediaOutsideScope(identity);
  }, [identity]);

  const sync = useCallback(async () => {
    if (!identity) return;

    if (!requiredMedia.length) {
      if (menus.length > 0) await pruneCatalogMediaScope(identity, []);
      setSyncError(null);
      return;
    }
    if (!online) return;

    const baseUrl = getGatewayBaseUrl();
    if (!baseUrl) {
      setSyncError("POS_CATALOG_MEDIA_GATEWAY_NOT_CONFIGURED");
      return;
    }

    const employeeSession = await readLocalEmployeeSession();
    if (!employeeSession?.sessionToken) return;

    const current = await readCatalogMediaForScope(identity);
    const localKeys = new Set(
      current.map((row) => catalogMediaReferenceKey(row.mediaAssetId, row.contentRevision)),
    );
    const missing = requiredMedia.filter(
      (media) => !localKeys.has(catalogMediaReferenceKey(media.media_asset_id, media.content_revision)),
    );

    if (!missing.length) {
      await pruneCatalogMediaScope(identity, requiredMedia);
      setSyncError(null);
      return;
    }

    const client = new PosCatalogMediaGatewayClient({ baseUrl });
    const deliveries: CatalogMediaDelivery[] = [];
    let firstError: string | null = null;

    // Current MORE COFFEE cold cache fits in one authority request. Chunking keeps the
    // same contract generic when future tenants/catalogs exceed the server batch cap.
    for (let offset = 0; offset < missing.length; offset += MEDIA_AUTH_BATCH_SIZE) {
      const slice = missing.slice(offset, offset + MEDIA_AUTH_BATCH_SIZE);
      const batch = await client.resolveBatch({
        deviceSecret: terminal.deviceSecret,
        sessionToken: employeeSession.sessionToken,
        media: slice.map((media) => ({
          media_asset_id: media.media_asset_id,
          content_revision: media.content_revision,
        })),
      });

      if (!batch.ok || !batch.delivery) {
        setSyncError(batch.error ?? "CATALOG_MEDIA_BATCH_RESOLVE_FAILED");
        return;
      }
      deliveries.push(...batch.delivery.items);
      if (batch.delivery.unavailable_count > 0 && firstError === null) {
        firstError = "CATALOG_MEDIA_SOME_NOT_AVAILABLE";
      }
    }

    const expectedByKey = new Map(
      missing.map((media) => [
        catalogMediaReferenceKey(media.media_asset_id, media.content_revision),
        media,
      ]),
    );
    let nextIndex = 0;

    const downloadWorker = async () => {
      while (true) {
        const index = nextIndex;
        nextIndex += 1;
        if (index >= deliveries.length) return;

        const delivery = deliveries[index];
        const expected = expectedByKey.get(
          catalogMediaReferenceKey(delivery.media_asset_id, delivery.content_revision),
        );
        if (!expected) {
          if (firstError === null) firstError = "CATALOG_MEDIA_BATCH_DELIVERY_REFERENCE_MISMATCH";
          continue;
        }

        try {
          const result = await client.downloadDelivery(delivery);
          if (!result.ok || !result.delivery || !result.blob) {
            throw new Error(result.error ?? "CATALOG_MEDIA_DOWNLOAD_FAILED");
          }
          await storeVerifiedCatalogMedia({
            identity,
            expected,
            delivery: result.delivery,
            blob: result.blob,
          });
        } catch (error) {
          if (firstError === null) {
            firstError = error instanceof Error
              ? error.message
              : "CATALOG_MEDIA_DOWNLOAD_FAILED";
          }
        }
      }
    };

    const workerCount = Math.min(mediaDownloadConcurrency, deliveries.length);
    await Promise.all(Array.from({ length: workerCount }, () => downloadWorker()));

    await pruneCatalogMediaScope(identity, requiredMedia);
    setSyncError(firstError);
  }, [identity, mediaDownloadConcurrency, menus.length, online, requiredKey, requiredMedia, terminal.deviceSecret]);

  useRuntimeAutoSync({
    enabled: Boolean(identity && online),
    sync,
    triggerKey: `${scopeKey}:${requiredKey}`,
    intervalMs: MEDIA_SYNC_INTERVAL_MS,
  });

  const mediaUrlFor = useCallback((media: CatalogProductMediaProjection | null | undefined) => {
    if (!media) return null;
    return objectUrls.get(catalogMediaReferenceKey(media.media_asset_id, media.content_revision)) ?? null;
  }, [objectUrls]);

  return {
    mediaUrlFor,
    sync,
    syncError,
    localMediaCount: objectUrls.size,
    requiredMediaCount: requiredMedia.length,
  };
}
